import React, { useEffect, useState } from 'react';
import { Row, Col, Tag, message } from 'antd';
import { updateStatus } from '@app/api/pre-clearance.api';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import useApiErrorHandler from '@app/components/Error/ErrorMsgHandling';

interface WorkflowButtonsProps {
  status: string | null;
  id: string | number | null;
  setStatus: (status: string | null) => void;
  wfActions?: string[]; // optional, comes from API
}

const DEFAULT_ACTION_LABELS: Record<string, string> = {
  DRAFT_TO_PENDING_DOCUMENT: 'Create Task',
  PENDING_DOCUMENT_TO_PENDING_REVIEW: 'Submit Documents',
  PENDING_REVIEW_TO_PENDING_DOCUMENT: 'Resubmit Documents',
  PENDING_REVIEW_TO_CANCEL: 'Cancel Request',
  PENDING_REVIEW_TO_PENDING_PRE_APPROVAL: 'Submit for Pre-Approval',
  PENDING_PRE_APPROVAL_TO_PENDING_APPROVAL: 'Submit for Approval',
  PENDING_APPROVAL_TO_DONE: 'Complete Process',
};

const WorkflowButtons: React.FC<WorkflowButtonsProps> = ({ status, id, setStatus, wfActions }) => {
  const [localWfActions, setLocalWfActions] = useState<string[]>(wfActions || []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleErrorMessages = useApiErrorHandler();

  useEffect(() => {
    setLocalWfActions(wfActions || []);
  }, [wfActions]);

  if (!id) return null;

  const handleStatusChange = async (action: string) => {
    if (!id) {
      message.error('Invalid request. Please refresh and try again.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await updateStatus(Number(id), { action });
      const updatedStatus = response?.pre_clearance?.status ?? action.split('_TO_')[1];
      setStatus(updatedStatus);

      message.success(`Status updated to ${updatedStatus}.`);
    } catch (error: any) {
      handleErrorMessages(error);
      console.error('Error updating status:', error);
      message.error('Failed to update status. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Row
      justify="space-between"
      align="middle"
      style={{
        padding: '16px 20px',
        background: '#fff',
        borderRadius: '12px',
        boxShadow: '0 6px 12px rgba(0,0,0,0.1)',
        minHeight: '72px',
      }}
    >
      <Col>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {localWfActions.length > 0 &&
            localWfActions.map((action) => {
              const label = DEFAULT_ACTION_LABELS[action] || 'Next Step';
              return (
                <BaseButton
                  key={action}
                  type="primary"
                  size="small"
                  onClick={() => handleStatusChange(action)}
                  disabled={isSubmitting}
                >
                  {label}
                </BaseButton>
              );
            })}
        </div>
      </Col>
      <Col>
        <span style={{ fontWeight: 600, marginRight: 8 }}>Status:</span>
        <Tag
          color={status === 'DONE' ? 'green' : status === 'CANCEL' ? 'red' : 'orange'}
          style={{ fontSize: 14, padding: '6px 12px', borderRadius: 8 }}
        >
          {status || 'N/A'}
        </Tag>
      </Col>
    </Row>
  );
};

export default WorkflowButtons;
